import type {
  IStorageService,
  ISyncStorageService,
} from '@personal-trainer-app/shared/src/types/common/storage.types';

/**
 * Web storage service implementation using localStorage
 * Implements async storage interface for browser environments
 */
class WebStorageService implements IStorageService {
  /**
   * Retrieves a value from localStorage
   */
  async get<T>(key: string): Promise<T | null> {
    try {
      const item = localStorage.getItem(key);
      if (item === null) {
        return null;
      }
      return JSON.parse(item) as T;
    } catch (error) {
      console.error(`Failed to get item from storage: ${key}`, error);
      return null;
    }
  }

  /**
   * Stores a value in localStorage
   */
  async set<T>(key: string, value: T): Promise<void> {
    try {
      const serialized = JSON.stringify(value);
      localStorage.setItem(key, serialized);
    } catch (error) {
      console.error(`Failed to set item in storage: ${key}`, error);
      throw new Error(`Failed to store item: ${key}`);
    }
  }

  /**
   * Removes a value from localStorage
   */
  async remove(key: string): Promise<void> {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error(`Failed to remove item from storage: ${key}`, error);
      throw new Error(`Failed to remove item: ${key}`);
    }
  }

  /**
   * Clears all values from localStorage
   */
  async clear(): Promise<void> {
    try {
      localStorage.clear();
    } catch (error) {
      console.error('Failed to clear storage', error);
      throw new Error('Failed to clear storage');
    }
  }

  /**
   * Checks if a key exists in localStorage
   */
  async has(key: string): Promise<boolean> {
    try {
      return localStorage.getItem(key) !== null;
    } catch (error) {
      console.error(`Failed to check if key exists: ${key}`, error);
      return false;
    }
  }

  /**
   * Gets all localStorage keys
   */
  async keys(): Promise<string[]> {
    try {
      const keys: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key !== null) {
          keys.push(key);
        }
      }
      return keys;
    } catch (error) {
      console.error('Failed to get storage keys', error);
      return [];
    }
  }
}

/**
 * Synchronous web storage service backed by localStorage.
 *
 * Registered via `registerSyncStorageService` at app startup so the shared
 * zustand `persist` stores hydrate SYNCHRONOUSLY (matching localStorage's
 * semantics — no logged-out flash on load). Same JSON serialization format as
 * WebStorageService, so previously-persisted data round-trips unchanged.
 * Writes log on failure rather than throwing, so a persist write never breaks a
 * store update.
 */
class WebSyncStorageService implements ISyncStorageService {
  get<T>(key: string): T | null {
    try {
      const item = localStorage.getItem(key);
      return item === null ? null : (JSON.parse(item) as T);
    } catch (error) {
      console.error(`Failed to get item from storage: ${key}`, error);
      return null;
    }
  }

  set<T>(key: string, value: T): void {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.error(`Failed to set item in storage: ${key}`, error);
    }
  }

  remove(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error(`Failed to remove item from storage: ${key}`, error);
    }
  }

  clear(): void {
    try {
      localStorage.clear();
    } catch (error) {
      console.error('Failed to clear storage', error);
    }
  }

  has(key: string): boolean {
    try {
      return localStorage.getItem(key) !== null;
    } catch (error) {
      console.error(`Failed to check if key exists: ${key}`, error);
      return false;
    }
  }

  keys(): string[] {
    try {
      const keys: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key !== null) {
          keys.push(key);
        }
      }
      return keys;
    } catch (error) {
      console.error('Failed to get storage keys', error);
      return [];
    }
  }
}

// Export classes for dependency injection
export { WebStorageService, WebSyncStorageService };

// Export singleton instance
export const webStorageService = new WebStorageService();
export default webStorageService;
